import type { JSX, ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';
import type { PageSlice } from './page-slice';
import { cx } from './primitives';

/**
 * The chrome around a data table: one toolbar, one pager, one full-height card.
 *
 * Every table in this platform wears the same one. Before it, each screen laid
 * out its own controls with `FormField`, which stacks a label ABOVE its control -
 * so a search box with a label sat 22px lower than a select whose label wrapped
 * differently, and the band above a table came out ragged on every screen and
 * ragged in a different way on each. Here the controls are siblings on one row
 * at one height by construction, and no call site is in a position to get it
 * wrong.
 *
 * Layout, fixed: FILTERS on the left, SEARCH on the right, the pager at the
 * BOTTOM right of the table itself, on the band that also carries the row
 * count. The shape is the one used by the Invoices and Debtors tables in
 * smb-voice, built here on this platform's own primitives - there is no
 * Tailwind and no icon package in this dependency budget, so the glyphs are
 * inline SVG.
 */

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

/** What the toolbar needs of a `useTable` result to draw the box itself. */
export interface TableToolbarSearch {
  readonly query: string;
  readonly setQuery: (next: string) => void;
}

interface TableToolbarSlots {
  /** Left. Filters, in the order a reader narrows by. */
  readonly filters?: ReactNode;
  /** Right. The search box - or give `table` and the toolbar draws it. */
  readonly search?: ReactNode;
}

/**
 * `table` and `searchLabel` travel together or not at all.
 *
 * Two arms rather than three optional props, because a box the toolbar draws
 * takes its accessible name from the caller and there is no name it could
 * invent: `searchLabel` merely optional meant an omitted one rendered
 * `aria-label=""`, an unnamed search box, silently. Here the omission does not
 * compile.
 */
export type TableToolbarProps = TableToolbarSlots &
  (
    | {
        /** A `useTable` result: the toolbar renders the search box from it. */
        readonly table: TableToolbarSearch;
        /** Accessible name of that box. Already translated. */
        readonly searchLabel: string;
        readonly searchPlaceholder?: string;
      }
    | {
        readonly table?: undefined;
        readonly searchLabel?: undefined;
        readonly searchPlaceholder?: undefined;
      }
  );

/**
 * The band above a table.
 *
 * `align-items: center` and a fixed control height are what keep it level. The
 * spacer between the two groups is a flex gap rather than a margin, so a wrapped
 * row still reads as filters-then-search rather than drifting apart.
 *
 * Handed a `useTable` result it draws the box itself, because a search that
 * narrows the rows already held is the same three lines on every screen. The
 * `search` slot stays for the screens whose box drives a server parameter
 * through their own state, and it wins when both are given.
 */
export function TableToolbar(props: TableToolbarProps): JSX.Element {
  // Read off `props` rather than destructured, so narrowing on `table` carries
  // `searchLabel` with it - destructuring separates them and loses the pair.
  const box =
    props.search !== undefined ? (
      props.search
    ) : props.table === undefined ? undefined : (
      <TableSearch
        label={props.searchLabel}
        value={props.table.query}
        onValueChange={props.table.setQuery}
        {...(props.searchPlaceholder === undefined ? {} : { placeholder: props.searchPlaceholder })}
      />
    );
  return (
    <div className="cib-toolbar">
      <div className="cib-toolbar__filters">{props.filters}</div>
      {box !== undefined ? <div className="cib-toolbar__search">{box}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

export interface TableControlProps {
  /** Sits inside the control, before the value - never stacked above it. */
  readonly label: string;
  /** Receives the id the label points at, exactly as `FormField` does. */
  readonly children: (control: { readonly id: string; readonly className: string }) => ReactNode;
}

/**
 * A filter that is not a select - a date, a number, a checkbox.
 *
 * Same wrapper, same height, so a date range and a status dropdown sit level.
 * The child gets its own id and the borderless class rather than being styled
 * by the caller, because "style it like the others" is exactly the instruction
 * that produced five nearly-identical toolbars.
 */
export function TableControl({ label, children }: TableControlProps): JSX.Element {
  const id = useId();
  return (
    <span className="cib-filter">
      <label className="cib-filter__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, className: 'cib-filter__select' })}
    </span>
  );
}

export interface TableFilterProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Sits inside the control, before the value - never stacked above it. */
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * One filter: its name and its value in a single control of fixed height.
 *
 * The name is INSIDE the box. Stacked above, it made the control two lines tall
 * while a neighbouring one was one line, which is exactly the raggedness this
 * component exists to remove - and it is a real label element bound to the
 * select, so the name is still announced rather than merely drawn.
 */
export function TableFilter({
  label,
  children,
  className,
  ...rest
}: TableFilterProps): JSX.Element {
  const id = useId();
  return (
    <span className="cib-filter">
      <label className="cib-filter__label" htmlFor={id}>
        {label}
      </label>
      <select {...rest} id={id} className={cx('cib-filter__select', className)}>
        {children}
      </select>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export interface TableSearchProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  /** Accessible name. The placeholder is a hint and never the only label. */
  readonly label: string;
  readonly placeholder?: string;
}

export function TableSearch({
  value,
  onValueChange,
  label,
  placeholder,
}: TableSearchProps): JSX.Element {
  return (
    <span className="cib-search">
      <svg className="cib-search__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M13.5 13.5 L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        className="cib-search__input"
        aria-label={label}
        {...(placeholder === undefined ? {} : { placeholder })}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pager
// ---------------------------------------------------------------------------

export interface TablePagerLabels {
  /** Accessible name of the whole control. */
  readonly pagination: string;
  readonly first: string;
  readonly previous: string;
  readonly next: string;
  readonly last: string;
  /** Renders the range, e.g. "1-100 of 271". */
  readonly range: (from: number, to: number, total: string) => string;
}

export interface TablePagerProps<Row> {
  readonly slice: PageSlice<Row>;
  readonly onPage: (page: number) => void;
  readonly labels: TablePagerLabels;
  /**
   * Fetch the next cursor page and append it, for a screen that owns its query.
   * Given one, Next stays live at the end of the rows held instead of dying
   * there - which is what a `limit` on the endpoint costs if nothing follows the
   * cursor: rows that exist, belong to the reader, and cannot be reached.
   * Omitted, this pager behaves exactly as it always has.
   */
  readonly onMore?: () => void;
  /** A cursor fetch is in flight; the steps rest rather than queueing another. */
  readonly loadingMore?: boolean;
}

const ARROWS = {
  first: 'M11 4 L6 10 L11 16 M15 4 L10 10 L15 16',
  previous: 'M13 4 L7 10 L13 16',
  next: 'M7 4 L13 10 L7 16',
  last: 'M9 4 L14 10 L9 16 M5 4 L10 10 L5 16',
} as const;

/**
 * The numbers and the arrows, at the bottom right of the table.
 *
 * It renders even for a single page: a control that appears only once a list
 * grows past 100 rows is a control nobody finds, and its absence is read as "the
 * table cannot be paged" rather than "this one fits". The arrows are disabled
 * BUTTONS rather than removed, so the row keeps its width and nothing under it
 * shifts as the reader moves through pages.
 */
export function TablePager<Row>({
  slice,
  onPage,
  labels,
  onMore,
  loadingMore = false,
}: TablePagerProps<Row>): JSX.Element {
  const total = slice.more ? `${slice.total}+` : String(slice.total);
  /*
   * Next has two jobs on a screen that owns its query: move within the rows
   * held, and - at the end of them - ask for the next cursor page. Last keeps
   * only the first: "last" over a cursor list is a page nobody has fetched and
   * nobody can name, and jumping to the end of what happens to be loaded while
   * calling it the end of the list is the same untruth as a bare total over a
   * truncated fetch.
   */
  const fetchable = slice.canFetchMore && onMore !== undefined && !loadingMore;
  const step = (
    key: keyof typeof ARROWS,
    page: number,
    enabled: boolean,
    label: string,
  ): JSX.Element => (
    <button
      type="button"
      className="cib-pager__step"
      aria-label={label}
      disabled={!enabled}
      onClick={() => {
        /*
         * At the end of the rows held, Next does BOTH: asks for the next
         * cursor page and moves onto it.
         *
         * It used to only fetch, which cost the reader a second click for
         * every page - press once and the rows arrive underneath while the
         * pager sits where it was, press again and it finally moves (founder,
         * 2026-09-07). Setting the page before its rows exist is safe:
         * `pageSlice` clamps only what it DISPLAYS, and the page a screen holds
         * is the number asked for, so the move lands the moment the fetch does.
         */
        if (key === 'next' && !slice.canNext && fetchable) onMore();
        onPage(page);
      }}
    >
      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path
          d={ARROWS[key]}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  return (
    <nav className="cib-pager" aria-label={labels.pagination}>
      {/*
       * Polite, not assertive: paging is the reader's own action and they are
       * looking at the result of it. It is announced because the rows change
       * under a heading that does not.
       */}
      <span className="cib-pager__range" aria-live="polite">
        {labels.range(slice.from, slice.to, total)}
      </span>
      <span className="cib-pager__steps">
        {step('first', 1, slice.canPrev, labels.first)}
        {step('previous', slice.page - 1, slice.canPrev, labels.previous)}
        {step('next', slice.page + 1, slice.canNext || fetchable, labels.next)}
        {step('last', slice.totalPages, slice.canNext, labels.last)}
      </span>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// The table's own band and card
// ---------------------------------------------------------------------------

export interface TableBandProps {
  /** Left: what the table holds, e.g. "3 apartments". */
  readonly caption?: ReactNode;
  /** Right: the pager. */
  readonly pager?: ReactNode;
}

/** The strip immediately below the table: count on the left, pager far right. */
export function TableBand({ caption, pager }: TableBandProps): JSX.Element {
  return (
    <div className="cib-tableband">
      <span className="cib-tableband__caption">{caption}</span>
      {pager}
    </div>
  );
}
